import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Container,
  Grid,
  Typography,
  Box,
  Divider,
  alpha,
  Paper,
} from '@mui/material';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import PATHS from 'lib/navigation/paths';
import useAppNavigator from 'views/rebranded-module/common/hooks/useAppNavigator';

const PREFIX = 'HowRentingWorks';

const classes = {
  mainContainer: `${PREFIX}-mainContainer`,
  title: `${PREFIX}-title`,
  subTitle: `${PREFIX}-subTitle`,
  textContainer: `${PREFIX}-textContainer`,
  stepContainer: `${PREFIX}-stepContainer`,
  stepNumber: `${PREFIX}-stepNumber`,
  stepTitle: `${PREFIX}-stepTitle`,
  stepDesc: `${PREFIX}-stepDesc`,
  imgContainer: `${PREFIX}-imgContainer`,
  img: `${PREFIX}-img`,
  divider: `${PREFIX}-divider`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({ theme }) => ({
  [`& .${classes.mainContainer}`]: {
    marginBottom: theme.spacing(13),
    paddingTop: theme.spacing(14),
    maxWidth: theme.spacing(166.25),
    [theme.breakpoints.up('lg')]: {
      marginBottom: theme.spacing(26),
      paddingTop: theme.spacing(22),
    },
    [theme.breakpoints.only('sm')]: {
      marginBottom: theme.spacing(5.5),
      paddingTop: theme.spacing(9),
    },
  },

  [`& .${classes.title}`]: {
    fontSize: theme.typography.pxToRem(22),
    marginBottom: theme.spacing(0),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(36),
      marginBottom: theme.spacing(2),
      textAlign: 'left',
    },
    [theme.breakpoints.up('lg')]: {
      textAlign: 'center',
    },
  },

  [`& .${classes.subTitle}`]: {
    color: theme.palette.grey[300],
    fontSize: theme.typography.pxToRem(18),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(32),
      marginBottom: theme.spacing(6),
      textAlign: 'left',
    },
    [theme.breakpoints.up('lg')]: {
      textAlign: 'center',
    },
  },

  [`& .${classes.textContainer}`]: {
    borderLeft: `1px dashed ${theme.palette.grey[200]}`,
    marginLeft: theme.spacing(2.5),
    [theme.breakpoints.only('sm')]: {
      marginLeft: theme.spacing(1.5),
    },
  },

  [`& .${classes.stepContainer}`]: {
    marginLeft: theme.spacing(-2.5),
    width: '85%',
    padding: theme.spacing(4.5, 0, 2),
    [theme.breakpoints.only('sm')]: {
      marginLeft: theme.spacing(-1.5),
      width: 'auto',
    },
  },

  [`& .${classes.stepNumber}`]: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    color: theme.palette.primary.main,
    borderRadius: 10,
    boxShadow: `0 0 4px 0 ${alpha(theme.palette.common.black, 0.15)}`,
    border: `3px solid ${theme.palette.primary.main}`,
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 'bold',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.pxToRem(14),
      width: theme.spacing(3),
      height: theme.spacing(3),
      borderRadius: 3,
    },
  },

  [`& .${classes.stepTitle}`]: {
    marginLeft: theme.spacing(2),
    fontSize: theme.typography.pxToRem(24),
    fontWeight: '500',
    lineHeight: theme.typography.pxToRem(40),
    [theme.breakpoints.only('sm')]: {
      fontSize: theme.typography.pxToRem(18),
      lineHeight: theme.typography.pxToRem(24),
    },
    '& span': {
      color: theme.palette.grey[400],
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 'normal',
    },
  },

  [`& .${classes.stepDesc}`]: {
    marginLeft: theme.spacing(7),
    letterSpacing: theme.typography.pxToRem(0.5),
    lineHeight: theme.typography.pxToRem(24),
    marginTop: theme.spacing(1),
    fontSize: theme.typography.pxToRem(14),
    [theme.breakpoints.only('sm')]: {
      marginLeft: theme.spacing(5),
    },
    '& a': {
      color: theme.palette.common.black,
      fontWeight: 700,
    },
  },

  [`& .${classes.imgContainer}`]: {
    height: theme.spacing(42.5),
    marginTop: theme.spacing(5.5),
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 20,
    [theme.breakpoints.up('lg')]: {
      height: '96%',
      marginTop: theme.spacing(4.5),
    },
    [theme.breakpoints.only('sm')]: {
      height: theme.spacing(20),
      marginTop: theme.spacing(2.75),
      borderRadius: 15,
    },
  },

  [`& .${classes.img}`]: {
    top: '-15% !important',
    minHeight: '100% !important',
    maxWidth: '100% !important',
    position: 'absolute !important',
    margin: '0 !important',
    height: 'auto !important',
    maxHeight: 'none !important',
    width: 'auto !important',
    [theme.breakpoints.up('lg')]: {
      height: '100% !important',
      right: '-15% !important',
      maxWidth: 'none !important',
      top: '0px !important',
      left: 'auto !important',
    },
  },

  [`& .${classes.divider}`]: {
    marginTop: theme.spacing(10),
    backgroundColor: theme.palette.grey[200],
    [theme.breakpoints.only('sm')]: {
      marginTop: theme.spacing(5.5),
    },
  },
}));

const HowRentingWorks = () => {
  const { t } = useTranslation('fe_er_rent_my_motorcycle');
  const appNavigator = useAppNavigator();

  return (
    <Root>
      <Container classes={{ root: classes.mainContainer }}>
        <Typography variant="h2" classes={{ root: classes.title }}>
          {t('fe_er_rent_my_motorcycle:how_renting_works_title')}
        </Typography>
        <Typography variant="h3" classes={{ root: classes.subTitle }}>
          {t('fe_er_rent_my_motorcycle:how_renting_works_sub_title')}
        </Typography>
        <Grid container alignItems="stretch" spacing={2}>
          <Grid item xs={12} lg={7}>
            <Box className={classes.textContainer}>
              <Box className={classes.stepContainer}>
                <Grid
                  container
                  wrap="nowrap"
                  justifyContent="flex-start"
                  alignItems="center">
                  <Grid item>
                    <Box className={classes.stepNumber}>1</Box>
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="h3"
                      classes={{ root: classes.stepTitle }}>
                      {t('fe_er_rent_my_motorcycle:steps.title1')}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography classes={{ root: classes.stepDesc }}>
                  {t('fe_er_rent_my_motorcycle:steps.desc1')}
                </Typography>
              </Box>

              <Box className={classes.stepContainer}>
                <Grid
                  container
                  wrap="nowrap"
                  justifyContent="flex-start"
                  alignItems="center">
                  <Grid item>
                    <Box className={classes.stepNumber}>2</Box>
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="h3"
                      classes={{ root: classes.stepTitle }}>
                      {t('fe_er_rent_my_motorcycle:steps.title2')}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography classes={{ root: classes.stepDesc }}>
                  {t('fe_er_rent_my_motorcycle:steps.desc2')}
                </Typography>
              </Box>

              <Box className={classes.stepContainer}>
                <Grid
                  container
                  wrap="nowrap"
                  justifyContent="flex-start"
                  alignItems="center">
                  <Grid item>
                    <Box className={classes.stepNumber}>3</Box>
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="h3"
                      classes={{ root: classes.stepTitle }}>
                      {t('fe_er_rent_my_motorcycle:steps.title3')}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography classes={{ root: classes.stepDesc }}>
                  {t('fe_er_rent_my_motorcycle:steps.desc3')}
                </Typography>
              </Box>

              <Box className={classes.stepContainer}>
                <Grid
                  container
                  wrap="nowrap"
                  justifyContent="flex-start"
                  alignItems="center">
                  <Grid item>
                    <Box className={classes.stepNumber}>4</Box>
                  </Grid>
                  <Grid item xs>
                    <Typography
                      variant="h3"
                      classes={{ root: classes.stepTitle }}>
                      {t('fe_er_rent_my_motorcycle:steps.title4')}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography classes={{ root: classes.stepDesc }}>
                  {t('fe_er_rent_my_motorcycle:steps.desc4')}
                </Typography>

                <Typography
                  classes={{ root: classes.stepDesc }}
                  dangerouslySetInnerHTML={{
                    __html: t('fe_er_rent_my_motorcycle:steps.desc4_2', {
                      club_path: appNavigator.getUrl(
                        undefined,
                        PATHS.motorcycleClub.index().url,
                        true,
                      ),
                      commercial_host_path:
                        'https://www.eagleshare.com/commercial-host',
                    }),
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Box className={classes.imgContainer}>
              <Image
                className={classes.img}
                src="/imgs/list-your-bike/header.jpg"
                alt={t('fe_er_rent_my_motorcycle:title')}
                layout="fill"
                unoptimized
              />
            </Box>
          </Grid>
        </Grid>
        <Paper sx={{ display: { md: 'block', lg: 'none' } }}>
          <Divider classes={{ root: classes.divider }} />
        </Paper>
      </Container>
    </Root>
  );
};

export default HowRentingWorks;
